import React from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { $createParagraphNode, $getRoot } from 'lexical';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import {
  $convertFromMarkdownString,
  TRANSFORMERS,
  type Transformer,
} from '@lexical/markdown';
import { CodeNode } from '@lexical/code';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

// Import these from the memory bank note editor to reuse the theme and plugins
import NoteEditorTheme from '../memory-bank/lexical/NoteEditorTheme';
import { ToolbarPlugin } from '../memory-bank/lexical/plugins/ToolbarPlugin';
import { ListMaxIndentLevelPlugin } from '../memory-bank/lexical/plugins/ListMaxIndentLevelPlugin';
import { AutoLinkPlugin } from '../memory-bank/lexical/plugins/AutoLinkPlugin';
import { OnChangeMarkdown } from '../memory-bank/lexical/plugins/OnChangeMarkdown';
import { KeyboardShortcutsPlugin } from '../memory-bank/lexical/plugins/KeyboardShortcutsPlugin';
import '../memory-bank/lexical/lexical-editor.css';
import { TABLE } from './TableTransformer';
import { ImageNode } from './ImageNode';
import { IMAGE } from './ImageTransformer';

const TRANSFORMERS_WITH_TABLES: Transformer[] = [TABLE, IMAGE, ...TRANSFORMERS];

export interface MarkdownWYSIWYGEditorProps {
  initialValue?: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: number;
  showToolbar?: boolean;
  readonly?: boolean;
}

function Placeholder({ placeholder }: { placeholder: string }) {
  return (
    <div className="absolute top-3 left-3 text-gray-400 pointer-events-none select-none">
      {placeholder}
    </div>
  );
}

const editorConfig = {
  namespace: 'markdown-wysiwyg-editor',
  // The editor theme
  theme: NoteEditorTheme,
  // Handling of errors during update
  onError(error: Error) {
    console.error('Lexical editor error:', error);
  },
  // Any custom nodes go here
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
    CodeNode,
    ImageNode,
  ],
};

export function MarkdownWYSIWYGEditor({
  initialValue = '',
  readonly = false,
  placeholder = 'Write your note here...',
  height = 200,
  showToolbar = true,
  onChange,
}: MarkdownWYSIWYGEditorProps) {
  return (
    <LexicalComposer
      initialConfig={{
        ...editorConfig,
        editorState: () => {
          let str = (initialValue || '').replace(/\n\n<br>\n/g, '\n');

          // If we still have br tags, clean them up
          if (str.match(/<br>/g)) {
            str = str.replace(/^(\n)(?=\s*[-+\d.])/gm, '').replace(/<br>/g, '');
          }

          str = str
            // Unescape HTML characters
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&#39;/g, "'")
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');

          if (!str) {
            // Initialize with empty paragraph for proper focus behavior
            const root = $getRoot();
            const paragraph = $createParagraphNode();
            root.append(paragraph);
            return;
          }

          $convertFromMarkdownString(str, TRANSFORMERS_WITH_TABLES);
        },
      }}
    >
      <div
        className={`border border-gray-300 rounded-md overflow-hidden bg-white`}
      >
        {showToolbar && <ToolbarPlugin />}
        <div className="relative" style={{ minHeight: height }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="lexical-editor outline-none resize-none p-3 text-sm leading-relaxed"
                style={{ minHeight: height }}
                readOnly={readonly}
              />
            }
            placeholder={<Placeholder placeholder={placeholder} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
      </div>

      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <TablePlugin />
      <OnChangeMarkdown onChange={onChange} />
      <AutoLinkPlugin />
      <ListMaxIndentLevelPlugin maxDepth={7} />
      <MarkdownShortcutPlugin transformers={TRANSFORMERS_WITH_TABLES} />
      <KeyboardShortcutsPlugin />
    </LexicalComposer>
  );
}
